export interface LeadPhoneNumber {
  id: string;
  leadId: string;
  campaignId: string;
  phoneNumber: string;
  status: string;
  callAttempts: number;
  isActive: boolean;
  isVerified: boolean;
  isPrimary: boolean;
  lastCalledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Lead {
  id: string;
  campaignId: string;
  externalRef?: string;
  fullName: string;
  phoneE164: string;
  areaCode?: string | null;
  timezone?: 'Eastern' | 'Central' | 'Mountain' | 'Pacific' | 'Unknown';
  email?: string;
  companyName?: string;
  custom: Record<string, any>;
  status: LeadStatus;
  lastCallAt?: string;
  createdAt: string;
  updatedAt: string;
  statusChangedAt?: string;
  status_changed_at?: string;
  callAttempts?: CallAttempt[];
  latestAttempt?: CallAttempt;
  doNotCall: boolean;
  notes?: string;
  followUpDate?: string;
  callAttemptsCount?: number;
  phoneNumbers?: LeadPhoneNumber[];
  carrier?: string | null;
  carrierName?: string | null;
  lineType?: string | null;
  carrierLookupAt?: string | null;
  // Extended fields for global views
  latest_reason?: string;
  latest_call_status?: string;
  latestCallStatus?: string;
  latest_transcript?: string;
  campaign_name?: string;
  campaignName?: string;
  // SMS fields (text campaigns only)
  smsEnrollmentStatus?: string | null;
  smsCurrentStep?: number;
  smsSentCount?: number;
  smsRepliedCount?: number;
  lastSmsAt?: string | null;
  smsDeliveredAt?: string | null;
  smsLastOutboundStatus?: string | null;
  subStageId?: string | null;
  subStage?: LeadSubStage | null;
  sub_stage?: LeadSubStage | null;
  sub_stage_name?: string | null;
  assignedAgentId?: string | null;
  assignedAgent?: TaggedAgent | null;
  assigned_agent?: TaggedAgent | null;
  taggedAgents?: TaggedAgent[];
  tagged_agents?: TaggedAgent[];
}

export interface LeadSubStage {
  id: string;
  mainStatus: string;
  name: string;
  slug: string;
  sortOrder?: number;
  allowsFollowUp?: boolean;
  color?: string | null;
}

export interface LeadFollowUp {
  id: string;
  leadId: string;
  scheduledAt: string;
  notes: string | null;
  createdBy: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeadNote {
  id: string;
  leadId: string;
  body: string;
  createdBy: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeadTimelineEvent {
  id: string;
  type: string;
  createdAt: string;
  createdByName?: string | null;
  payload: Record<string, unknown>;
}

export interface TaggedAgent {
  id: string;
  name: string;
  email?: string;
}

export type LeadStatus =
  | 'new'
  | 'called'
  | 'queued'
  | 'calling'
  | 'completed'
  | 'failed'
  | 'interested'
  | 'not_interested'
  | 'do_not_call'
  | 'invalid'
  | 'no_answer'
  | 'unreachable'
  | 'follow_up'
  | 'out_of_business'
  | 'wrong_number'
  | 'unqualified'
  | 'busy'
  | 'app_in'
  | 'gatekeeper'
  | 'operator'
  | 'app_out'
  | 'deceased'
  | 'spanish'
  | 'submitted'
  | 'contract'
  | 'funded';

export interface CallAttempt {
  id: string;
  lead_id: string;
  campaign_id: string;
  telnyx_call_control_id?: string;
  call_provider?: 'telnyx';
  amd_result?: 'human' | 'machine' | 'not_sure' | 'fax' | null;
  status: 'queued' | 'calling' | 'completed' | 'failed' | 'cancelled';
  conversation_style_id?: string;
  captured_params?: Record<string, any>;
  reason?: string;
  started_at?: Date;
  finished_at?: Date;
  duration_seconds?: number;
  cost?: number;
  analysis_summary?: string;
  transcript?: string;
  recording_url?: string;
  agent_name?: string | null;
  agentName?: string | null;
  created_at: Date;
  updated_at: Date;
}

export type CallStatus = 'pending' | 'queued' | 'ringing' | 'in_progress' | 'completed' | 'failed' | 'no_answer' | 'busy' | 'cancelled';
export type CallResult = 'interested' | 'not_interested' | 'callback_requested' | 'wrong_number' | 'voicemail' | 'hung_up' | 'technical_error';

export interface CreateLeadData {
  fullName: string;
  phoneE164: string;
  email?: string;
  companyName?: string;
  custom?: Record<string, any>;
  externalRef?: string;
  phoneNumbers?: string[];
}

export interface UpdateLeadData {
  fullName?: string;
  phoneE164?: string;
  email?: string;
  companyName?: string;
  custom?: Record<string, any>;
  status?: LeadStatus;
  notes?: string;
  followUpDate?: string | null;
  phoneNumbers?: string[];
  subStageId?: string | null;
  assignedAgentId?: string | null;
  taggedAgentIds?: string[];
}

export interface ImportLeadsData {
  file: File;
  mapping: FieldMapping;
  skipDuplicates: boolean;
  updateExisting: boolean;
}

export interface FieldMapping {
  fullName: string;
  phoneE164: string;
  email?: string;
  companyName?: string;
  externalRef?: string;
  custom?: Record<string, string>;
}

export interface LeadFilters {
  status?: LeadStatus[];
  timezone?: 'Eastern' | 'Central' | 'Mountain' | 'Pacific' | 'Unknown' | 'all';
  search?: string;
  hasEmail?: boolean;
  lastCallBefore?: string;
  lastCallAfter?: string;
  createdBefore?: string;
  createdAfter?: string;
  sortBy?: 'fullName' | 'phoneE164' | 'status' | 'lastCallAt' | 'createdAt' | 'updatedAt' | 'statusChangedAt';
  sortOrder?: 'asc' | 'desc';
  subStageId?: string;
  agentId?: string;
  campaignId?: string;
  followUpDateFrom?: string;
  followUpDateTo?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export interface LeadImportResult {
  total: number;
  imported: number;
  updated: number;
  skipped: number;
  errors: LeadImportError[];
  async?: boolean;
  importId?: string;
  status?: 'queued' | 'processing' | 'completed' | 'failed';
  estimatedRows?: number;
}

export interface LeadImportJobStatus extends LeadImportResult {
  id?: string;
  errorMessage?: string;
}

export interface LeadScrubImmediateResult {
  mode: 'immediate';
  processedLeads: number;
  updatedLeads: number;
  skippedLeads: number;
  leads?: Array<{
    id: string;
    carrier: string | null;
    carrierName: string | null;
    lineType: string | null;
    carrierLookupAt?: string | null;
    status?: string;
    doNotCall?: boolean;
  }>;
}

export interface LeadScrubJob {
  id: string;
  campaignId: string;
  jobType: 'phone_lookup' | 'dnc';
  statuses: string[];
  status: 'queued' | 'running' | 'completed' | 'failed';
  totalLeads: number;
  processedLeads: number;
  updatedLeads: number;
  skippedLeads: number;
  errorCount: number;
  errorMessage: string | null;
  lastLeadId: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  timezones?: string[];
}

export type LeadScrubStartResult = LeadScrubJob | LeadScrubImmediateResult;

export function isLeadScrubImmediateResult(
  data: LeadScrubStartResult
): data is LeadScrubImmediateResult {
  return 'mode' in data && data.mode === 'immediate';
}

export type ImportPhoneValidationStatus = 'new' | 'duplicate_in_file' | 'existing' | 'invalid';

export interface ImportPhoneValidationRow {
  row: number;
  phone: string;
  status: ImportPhoneValidationStatus;
  duplicateOfRow?: number;
  existingLeadName?: string;
  existingLeadId?: string;
}

export interface ImportPhoneValidationResult {
  summary: {
    total: number;
    newLeads: number;
    duplicateInFile: number;
    existingInCampaign: number;
    invalid: number;
  };
  rows: ImportPhoneValidationRow[];
}

export interface LeadImportError {
  row: number;
  field?: string;
  message: string;
  data: Record<string, any>;
}

export interface LeadExportOptions {
  format: 'csv' | 'xlsx';
  fields: string[];
  filters?: LeadFilters;
}

export interface BulkLeadAction {
  action: 'delete' | 'update_status' | 'do_not_call' | 'remove_do_not_call';
  leadIds: string[];
  data?: {
    status?: LeadStatus;
    notes?: string;
  };
}

export interface CallAttemptStats {
  totalCalls: number;
  uniqueLeadsDialed?: number;
  amdHuman?: number;
  /** Merchants who answered. Independent of AMD, which is off by default. */
  pickups?: number;
  answered: number;
  interested: number;
  notInterested: number;
  followUp: number;
  appIn: number;
  appOut: number;
  noAnswer: number;
  busy: number;
  gatekeeper: number;
  operator: number;
  wrongNumber: number;
  outOfBusiness: number;
  unqualified: number;
  doNotCall: number;
  deceased: number;
  spanish: number;
  unreachable: number;
  failed: number;
  calling: number;
  avgDuration: number;
  totalLeads: number;
  remainingLeads: number;
}

export interface ConversationStyleStats {
  conversationStyleId: string;
  conversationStyleName: string;
  totalCalls: number;
  interestedCount: number;
  notInterestedCount: number;
  interestRate: number;
}